import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { IconCode, IconCodeOff } from '@rossum/ui/icons/tabler';
import {
  Button,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { boldText, button } from '../../../../../../lib/formaterValues';
import { LookupCopilot } from '../LookupCopilot';
import type { LookupResult } from '../lookupResult';
import { AggregationSummary } from './AggregationSummary';
import { DatasetPicker } from './DatasetPicker';
import { MissingInfoEmptyState } from './MissingInfoEmptyState';
import type { LookupEditorProps } from './types';

/**
 * The lookup editor as it has always worked: every edit — the whole cascade or a single query —
 * happens in a one-shot dialog. Kept separate from the agentic editor so neither can reach the other.
 */
export const LegacyLookupEditor = ({
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  lookupLabel,
  isRequiredInfoMissing,
  table,
  tables,
  columnOptions,
  summaryPayload,
  summaryValue,
  isSourceEditorOpen,
  onToggleSourceEditor,
}: LookupEditorProps) => {
  const intl = useIntl();
  const [expandedQueries, setExpandedQueries] = useState<Set<number>>(
    new Set()
  );
  // One dialog for both jobs, told apart by its target — `null` is the whole cascade. Only `open`
  // is toggled on close: the dialog stays mounted for its exit transition, and dropping the target
  // would swap its title and summary mid-animation.
  const [copilot, setCopilot] = useState<{
    open: boolean;
    queryIndex: number | null;
  }>({ open: false, queryIndex: null });

  const closeCopilot = () => setCopilot(prev => ({ ...prev, open: false }));

  // Closing is the dialog's own business — it reports the result and nothing more.
  const applyResult = useCallback(
    (result: LookupResult) => {
      onChange({
        ...value,
        dataset: result.dataset || value.dataset,
        queries: result.queries,
        variables: result.variables,
      });
    },
    [onChange, value]
  );

  // Array.isArray: the stored configuration arrives through a cast, so it may not be a list at all.
  const hasSomeQueries =
    Array.isArray(value.queries) && value.queries.length > 0;

  const sourceCodeLabel = isSourceEditorOpen
    ? intl.formatMessage({ id: 'features.formulas.hideSourceCode' })
    : intl.formatMessage({ id: 'features.formulas.showSourceCode' });

  const openCascadeCopilot = () => setCopilot({ open: true, queryIndex: null });

  return (
    <>
      {isRequiredInfoMissing ? (
        <MissingInfoEmptyState
          lookupLabel={lookupLabel}
          lookupSchemaId={lookupSchemaId}
        />
      ) : (
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <DatasetPicker
              tables={tables}
              value={table}
              dataset={value.dataset}
              onChange={dataset => onChange({ ...value, dataset })}
            />
          </Stack>
          {hasSomeQueries ? (
            <AggregationSummary
              disabled={value.dataset === ''}
              fieldSchemaId={summaryPayload.fieldSchemaId}
              schemaContent={summaryPayload.schemaContent}
              onChange={queriesAndVariables =>
                onChange({ ...value, ...queriesAndVariables })
              }
              value={summaryValue}
              dataset={value.dataset}
              datasetName={table?.metadata?.name}
              expandedQueries={expandedQueries}
              onExpandedQueriesChange={setExpandedQueries}
              onQueryEdit={queryIndex => setCopilot({ open: true, queryIndex })}
            />
          ) : (
            <EmptyState
              title={intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.emptyState.title',
              })}
              description={intl.formatMessage(
                {
                  id: 'features.queueSettings.fields.lookup.emptyState.description',
                },
                { boldText, button: button(openCascadeCopilot) }
              )}
            />
          )}
        </Stack>
      )}
      <Stack direction="row" spacing={2} justifyContent="end">
        <Tooltip title={sourceCodeLabel}>
          <IconButton color="inherit" onClick={onToggleSourceEditor}>
            <SvgIcon fontSize="large">
              {isSourceEditorOpen ? <IconCodeOff /> : <IconCode />}
            </SvgIcon>
          </IconButton>
        </Tooltip>
        {hasSomeQueries ? (
          <Stack spacing={2} direction="row" alignItems="center">
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                const newIndex = value.queries.length;
                onChange({
                  ...value,
                  queries: [...value.queries, { aggregate: [] }],
                });
                setExpandedQueries(prev => new Set(prev).add(newIndex));
              }}
            >
              {intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.addLookup',
              })}
            </Button>
            <Button
              variant="contained"
              color="aurora"
              onClick={openCascadeCopilot}
            >
              {intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.editAll',
              })}
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            color="aurora"
            data-cy="create-lookup-button"
            disabled={isRequiredInfoMissing}
            onClick={openCascadeCopilot}
          >
            {intl.formatMessage({
              id: 'features.queueSettings.fields.lookup.createLookup',
            })}
          </Button>
        )}
      </Stack>
      <LookupCopilot
        open={copilot.open}
        onClose={closeCopilot}
        onResult={applyResult}
        fieldSchemaId={lookupSchemaId}
        schemaContent={schemaContent}
        isIterative={copilot.queryIndex !== null}
        queryIndex={copilot.queryIndex}
        dataset={value.dataset}
        // Only the cascade dialog may switch datasets.
        setDataset={
          copilot.queryIndex === null
            ? dataset => onChange({ ...value, dataset })
            : undefined
        }
        datasetColumns={columnOptions}
      />
    </>
  );
};
